import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { RadioButtonModule } from 'primeng/radiobutton';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { StaffDisputeDetail } from '../../services/staff-dispute.service';
import {
    buildCreditNoteSeedLines,
    CreditNoteOrderTypeId,
    CreditNoteSeedLine,
    defaultCreditQtyForLine,
    getCreditNoteTypesForDivision400
} from '../dispute.utils';

export interface CreditNoteDialogConfirm {
    creditOrderTypeId: CreditNoteOrderTypeId;
    seedLines: CreditNoteSeedLine[];
}

interface DialogLineRow {
    orderLineId: number;
    itemNumber: string;
    itemName: string;
    orderedQty: number;
    claimedQty: number;
    creditQty: number;
    rate?: number;
    included: boolean;
}

@Component({
    selector: 'app-dispute-credit-note-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        DialogModule,
        ButtonModule,
        TableModule,
        CheckboxModule,
        InputNumberModule,
        RadioButtonModule
    ],
    template: `
        <p-dialog
            header="Generate credit note"
            [(visible)]="visible"
            (visibleChange)="visibleChange.emit($event)"
            [modal]="true"
            [style]="{ width: 'min(720px, 96vw)' }"
            [draggable]="false"
        >
            <p class="dialog-intro" *ngIf="detail">
                Order <strong>{{ detail.orderNumber }}</strong> · Customer
                {{ detail.customerName || detail.customerId || '—' }}
            </p>

            <div class="type-row">
                <span class="type-label">Credit note type</span>
                <div class="type-options">
                    <div class="type-option" *ngFor="let t of creditTypes">
                        <p-radioButton
                            [inputId]="'cn-type-' + t"
                            name="creditType"
                            [value]="t"
                            [(ngModel)]="selectedType"
                        ></p-radioButton>
                        <label [for]="'cn-type-' + t">{{ t }}</label>
                    </div>
                </div>
            </div>

            <p-table [value]="rows" styleClass="dispute-table credit-note-lines-table" responsiveLayout="scroll">
                <ng-template pTemplate="header">
                    <tr>
                        <th class="col-include"></th>
                        <th>Item</th>
                        <th>Ordered</th>
                        <th>Claimed</th>
                        <th>Credit qty</th>
                        <th>Rate</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr>
                        <td>
                            <p-checkbox [(ngModel)]="row.included" [binary]="true"></p-checkbox>
                        </td>
                        <td>
                            <strong>{{ row.itemNumber }}</strong>
                            <div class="item-sub">{{ row.itemName }}</div>
                        </td>
                        <td>{{ row.orderedQty }}</td>
                        <td>{{ row.claimedQty }}</td>
                        <td>
                            <p-inputNumber
                                [(ngModel)]="row.creditQty"
                                [min]="0"
                                [max]="row.orderedQty > 0 ? row.orderedQty : undefined"
                                [minFractionDigits]="0"
                                [maxFractionDigits]="2"
                                [disabled]="!row.included"
                                inputStyleClass="credit-qty-input"
                            ></p-inputNumber>
                        </td>
                        <td>{{ row.rate != null ? (row.rate | number: '1.2-2') : '—' }}</td>
                    </tr>
                </ng-template>
            </p-table>

            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" (onClick)="close()"></p-button>
                <p-button label="Continue to order" icon="pi pi-arrow-right" (onClick)="onContinue()"></p-button>
            </ng-template>
        </p-dialog>
    `,
    styles: [
        `
            .dialog-intro {
                margin: 0 0 1rem;
                color: var(--dispute-muted, var(--text-color-secondary));
            }
            .type-row {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.75rem;
                margin-bottom: 1rem;
            }
            .type-label {
                font-size: 0.85rem;
                font-weight: 600;
            }
            .type-options {
                display: flex;
                gap: 1.25rem;
            }
            .type-option {
                display: flex;
                align-items: center;
                gap: 0.35rem;
            }
            .col-include {
                width: 3rem;
            }
            .item-sub {
                font-size: 0.78rem;
                color: var(--dispute-muted, var(--text-color-secondary));
            }
            .credit-note-lines-table {
                margin-bottom: 0.5rem;
            }
            :host ::ng-deep .credit-qty-input {
                width: 5rem;
            }
        `
    ]
})
export class DisputeCreditNoteDialogComponent implements OnChanges {
    constructor(private readonly messageService: MessageService) {}
    @Input() detail: StaffDisputeDetail | null = null;
    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() confirm = new EventEmitter<CreditNoteDialogConfirm>();

    readonly creditTypes = getCreditNoteTypesForDivision400();
    selectedType: CreditNoteOrderTypeId = 'NT3';
    rows: DialogLineRow[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['detail'] && this.detail) {
            this.selectedType = 'NT3';
            this.rows = (this.detail.lines || []).map((line) => ({
                orderLineId: line.orderLineId,
                itemNumber: line.itemNumber,
                itemName: line.itemName,
                orderedQty: line.orderedQty,
                claimedQty: line.claimedQty,
                creditQty: defaultCreditQtyForLine(line),
                rate: line.unitPriceEur,
                included: defaultCreditQtyForLine(line) > 0
            }));
        }
    }

    close(): void {
        this.visibleChange.emit(false);
    }

    onContinue(): void {
        const included = this.rows.filter((r) => r.included && r.creditQty > 0);
        if (!included.length) {
            this.messageService.add({
                severity: 'warn',
                summary: 'No lines selected',
                detail: 'Include at least one line with a credit quantity greater than zero.'
            });
            return;
        }
        const seedLines: CreditNoteSeedLine[] = included.map((r) => ({
            orderLineId: r.orderLineId,
            itemNumber: r.itemNumber,
            itemName: r.itemName,
            quantity: r.creditQty,
            rate: r.rate
        }));
        this.confirm.emit({
            creditOrderTypeId: this.selectedType,
            seedLines
        });
        this.visibleChange.emit(false);
    }
}
